import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Collapse,
  Flex,
  Heading,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AcceptedInquiryAlignment,
  Offer,
  OfferGroupBranch,
  OfferGroupBranchCurrency,
  offerApi,
} from '@texas/api/endpoints/offerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { HoverCard } from '@texas/components/shared/HoverCard';
import { InquiryHoverCard } from '@texas/components/shared/hovercards/InquiryHoverCard';
import { Icons } from '@texas/components/shared/Icons';
import { AddOfferCurrencyDrawer } from './AddOfferCurrencyDrawer';
import { OfferPriceTable } from './OfferPriceTable';
import { useOffer } from './OfferContext';
import { useTranslation } from 'react-i18next';

interface Props {
  branch: OfferGroupBranch;
}

export function OfferBranchCard({ branch }: Props) {
  const { t } = useTranslation();
  const {
    offerGroupId,
    group,
    findCurrency,
    settingMatch,
    removingCurrency,
    matchInquiry,
    unmatchInquiry,
    requestRemoveCurrency,
    openEditMasterOffer,
    openCopyingOffer,
    refreshGroup,
  } = useOffer();
  const matchedInquiries = branch.acceptedInquiries.filter((i) => i.isMatched);
  const matchedInquiry = matchedInquiries[0] ?? null;
  const hasAcceptedCurrency = branch.currencies.some(isCurrencyFullyAccepted);
  const customerId = group?.customerId ?? 0;

  return (
    <Card variant="dark">
      <CardHeader>
        <Heading size="sm">
          {branch.branchName}
          <Text
            as="span"
            fontWeight="normal"
            color="gray.400"
            ml={2}
            fontSize="sm"
          >
            {branch.branchIdentifier}
          </Text>
        </Heading>
        {hasAcceptedCurrency && (
          <Box mt={1}>
            <Badge colorScheme="green" fontSize="xs">
              <Icons.Checkmark boxSize={3} mr={1} />
              {t('general.accepted')}
            </Badge>
          </Box>
        )}
      </CardHeader>
      <CardBody>
        {branch.acceptedInquiries.length > 0 && (
          <AcceptedInquiriesCollapse
            acceptedInquiries={branch.acceptedInquiries}
            settingMatch={settingMatch}
            onSetMatched={matchInquiry}
            onClearMatched={unmatchInquiry}
            t={t}
          />
        )}

        <Tabs variant="line" size="sm">
          <Flex align="center" gap={2} mb={2}>
            <TabList>
              {branch.currencies.map((currency) => (
                <Tab key={currency.currencyId}>
                  <Flex align="center" gap={1}>
                    {currency.masterOffer?.isMain && <Icons.Flag boxSize={4} />}
                    <Text>
                      {findCurrency(currency.currencyId)?.code ??
                        String(currency.currencyId)}
                    </Text>
                    {isCurrencyFullyAccepted(currency) && (
                      <Icons.Checkmark boxSize={4} ml={1} color="green.300" />
                    )}
                    {currency.currencyId !== branch.branchDefaultCurrencyId &&
                      currency.masterOffer?.isMain !== true && (
                        <IconButton
                          aria-label={t('offer.removeCurrency')}
                          icon={<Icons.Close boxSize={3} />}
                          size="xs"
                          variant="ghost"
                          isLoading={removingCurrency}
                          onClick={(event) => {
                            event.stopPropagation();
                            requestRemoveCurrency(
                              branch.branchId,
                              currency.currencyId,
                            );
                          }}
                        />
                      )}
                  </Flex>
                </Tab>
              ))}
            </TabList>
            <AddOfferCurrencyDrawer
              offerGroupId={offerGroupId}
              branchId={branch.branchId}
              branchName={branch.branchName}
              customerId={customerId}
              existingCurrencies={branch.currencies}
              onAdded={refreshGroup}
            />
          </Flex>
          <TabPanels>
            {branch.currencies.map((currency) => (
              <TabPanel key={currency.currencyId} p={0} pt={2}>
                {currency.masterOffer && (
                  <MasterOfferInline
                    offer={currency.masterOffer}
                    findCurrency={findCurrency}
                    t={t}
                    isDefaultCurrency={
                      currency.currencyId === branch.branchDefaultCurrencyId
                    }
                    onEditClick={() =>
                      openEditMasterOffer(
                        currency.masterOffer!.id,
                        matchedInquiry,
                      )
                    }
                    onCopyClick={() =>
                      openCopyingOffer(currency.masterOffer!.id)
                    }
                    onSetMain={refreshGroup}
                    onArchivedChanged={refreshGroup}
                  />
                )}
                {currency.masterOffer?.archived ? (
                  <Text fontSize="sm" color="gray.400" mt={2}>
                    {t('inquiry.archived')}
                  </Text>
                ) : (
                  <Box mt={2}>
                    <OfferPriceTable
                      currencies={currency}
                      matchedInquiries={matchedInquiries}
                      findCurrency={findCurrency}
                      onPricesUpdated={refreshGroup}
                    />
                  </Box>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </CardBody>
    </Card>
  );
}

function AcceptedInquiriesCollapse({
  acceptedInquiries,
  settingMatch,
  onSetMatched,
  onClearMatched,
  t,
}: {
  acceptedInquiries: AcceptedInquiryAlignment[];
  settingMatch: boolean;
  onSetMatched: (inquiryId: number) => void;
  onClearMatched: (inquiryId: number) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const { isOpen, onToggle } = useDisclosure();
  const matchedCount = acceptedInquiries.filter(
    (inquiry) => inquiry.isMatched,
  ).length;

  return (
    <Box mb={4}>
      <Button
        variant="ghost"
        rightIcon={
          isOpen ? (
            <Icons.ChevronUp boxSize={4} />
          ) : (
            <Icons.ChevronDown boxSize={4} />
          )
        }
        onClick={onToggle}
      >
        {t('offer.acceptedInquiries')} ({matchedCount}/
        {acceptedInquiries.length})
      </Button>
      <Collapse in={isOpen} animateOpacity={true}>
        <Flex direction="column" gap={1} mt={2}>
          {acceptedInquiries.map((inq) => (
            <InquiryAlignmentRow
              key={inq.inquiryId}
              inquiry={inq}
              settingMatch={settingMatch}
              onSetMatched={() => onSetMatched(inq.inquiryId)}
              onClearMatched={() => onClearMatched(inq.inquiryId)}
              t={t}
            />
          ))}
        </Flex>
      </Collapse>
    </Box>
  );
}

function InquiryAlignmentRow({
  inquiry,
  settingMatch,
  onSetMatched,
  onClearMatched,
  t,
}: {
  inquiry: AcceptedInquiryAlignment;
  settingMatch: boolean;
  onSetMatched: () => void;
  onClearMatched: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const tierQuantities = [
    ...new Set(inquiry.tierPrices.map((tierPrice) => tierPrice.quantity)),
  ].sort((a, b) => a - b);

  return (
    <Flex
      align="center"
      gap={2}
      px={3}
      py={2}
      borderRadius="md"
      bg={inquiry.isMatched ? 'blue.900' : 'transparent'}
      _light={{
        borderColor: inquiry.isMatched ? 'blue.400' : 'gray.200',
        bg: inquiry.isMatched ? 'blue.50' : 'transparent',
      }}
    >
      <Box minW="0" flex="1">
        <HoverCard content={<InquiryHoverCard inquiryId={inquiry.inquiryId} />}>
          <Box>
            <AngularNavLink
              to="variant.inquiry.detail"
              navobj={{ inquiryId: inquiry.inquiryId }}
            >
              <Text fontSize="sm" fontWeight="medium">
                {inquiry.inquiryNumber}
              </Text>
            </AngularNavLink>
          </Box>
        </HoverCard>
        <Text fontSize="xs" color="gray.400">
          {inquiry.supplierName}
        </Text>
      </Box>
      <Flex gap={1} align="center" shrink={0}>
        {inquiry.isMain && (
          <Badge colorScheme="green" fontSize="2xs">
            {t('offer.main')}
          </Badge>
        )}
        {inquiry.isMatched && (
          <Badge colorScheme="blue" fontSize="2xs">
            {t('offer.matched')}
          </Badge>
        )}
      </Flex>
      <Flex gap={1} wrap="wrap" maxW="260px">
        {tierQuantities.map((quantity) => (
          <Tag key={quantity} size="sm" colorScheme="gray" variant="subtle">
            {quantity}
          </Tag>
        ))}
      </Flex>
      {inquiry.isMatched ? (
        <Button
          size="xs"
          variant="ghost"
          isLoading={settingMatch}
          onClick={onClearMatched}
          flexShrink={0}
        >
          {t('offer.unmatch')}
        </Button>
      ) : (
        <Button
          size="xs"
          variant="texas-light"
          isLoading={settingMatch}
          onClick={onSetMatched}
          flexShrink={0}
        >
          {t('offer.setMatched')}
        </Button>
      )}
    </Flex>
  );
}

function MasterOfferInline({
  offer,
  findCurrency,
  t,
  isDefaultCurrency,
  onEditClick,
  onCopyClick,
  onSetMain,
  onArchivedChanged,
}: {
  offer: Offer;
  findCurrency: (id: number) => { code: string } | undefined;
  t: (key: string) => string;
  isDefaultCurrency: boolean;
  onEditClick: () => void;
  onCopyClick: () => void;
  onSetMain: () => void;
  onArchivedChanged: () => void;
}) {
  const { request: setMain, loading: settingMain } = useApiRequest(
    offerApi.setMain,
  );
  const { request: archiveOffer, loading: archivingOffer } = useApiRequest(
    offerApi.archiveOffer,
  );
  const { request: restoreOffer, loading: restoringOffer } = useApiRequest(
    offerApi.restoreOffer,
  );
  const currencyCode = findCurrency(offer.currencyId)?.code ?? '';
  const isArchived = !!offer.archived;

  const handleSetMain = async () => {
    const res = await setMain(offer.id);
    if (!res.hasError) {
      onSetMain();
    }
  };

  const handleArchive = async () => {
    const res = await archiveOffer(offer.id);
    if (!res.hasError) {
      onArchivedChanged();
    }
  };

  const handleRestore = async () => {
    const res = await restoreOffer(offer.id);
    if (!res.hasError) {
      onArchivedChanged();
    }
  };

  return (
    <Box
      borderRadius="md"
      borderWidth="1px"
      borderColor={isArchived ? 'orange.500' : 'whiteAlpha.200'}
      p={3}
      bg="whiteAlpha.50"
      opacity={isArchived ? 0.65 : 1}
      _light={{
        bg: 'gray.50',
        borderColor: isArchived ? 'orange.300' : 'gray.200',
      }}
    >
      <Flex justify="space-between" align="flex-start">
        <Box>
          <Flex align="center" gap={2} mb={1}>
            {offer.isMain && (
              <Badge colorScheme="green" fontSize="xs">
                {t('offer.main')}
              </Badge>
            )}
            {isArchived && (
              <Badge colorScheme="orange" fontSize="xs">
                {t('inquiry.archived')}
              </Badge>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.400" mb={2}>
            {isDefaultCurrency ? '1' : offer.exchangeRate} {currencyCode}
            {' - '}
            {t('inquiry.sampleCharge')}: {offer.sampleCharge}
            {' - '}
            {t('inquiry.mouldCharge')}: {offer.mouldCharge}
            {' - '}
            {t('inquiry.surcharge')}: {offer.surcharge}
            {offer.mpq !== null && ` - MPQ: ${offer.mpq}`}
          </Text>
        </Box>
        <Flex gap={1}>
          {!offer.isMain && !isArchived && (
            <IconButton
              aria-label={t('offer.setMain')}
              icon={<Icons.Flag boxSize={4} />}
              size="xs"
              variant="ghost"
              isLoading={settingMain}
              onClick={handleSetMain}
            />
          )}
          {isArchived ? (
            <IconButton
              aria-label={t('general.restore')}
              icon={<Icons.Undo boxSize={4} />}
              size="xs"
              variant="texas-light"
              isLoading={restoringOffer}
              onClick={handleRestore}
            />
          ) : (
            <>
              <IconButton
                aria-label={t('offer.editMasterOffer')}
                icon={<Icons.Pencil boxSize={4} />}
                size="xs"
                variant="texas-light"
                onClick={onEditClick}
              />
              <Button
                size="xs"
                variant="texas-light"
                rightIcon={<Icons.ArrowRight boxSize={3} />}
                onClick={onCopyClick}
              >
                {t('offer.copyTo')}
              </Button>
              <IconButton
                aria-label={t('general.archive')}
                icon={<Icons.Archive boxSize={4} />}
                size="xs"
                variant="ghost"
                isLoading={archivingOffer}
                onClick={handleArchive}
              />
            </>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

function getCurrencyOffers(currencies: OfferGroupBranchCurrency) {
  return currencies.childOffers.length > 0
    ? currencies.childOffers
    : currencies.masterOffer
    ? [currencies.masterOffer]
    : [];
}

function isCurrencyFullyAccepted(currencies: OfferGroupBranchCurrency) {
  const tierPrices = getCurrencyOffers(currencies).flatMap(
    (offer) => offer.tierPrices,
  );

  return (
    tierPrices.length > 0 &&
    tierPrices.every((tierPrice) => tierPrice.acceptedDate != null)
  );
}
